import _ from 'lodash';
import CacheableDataObject from './CacheableDataObject';
import GroceryListItem from './GroceryListItem';
import moment from 'moment';
import MultiMap from "../utilities/MultiMap.js";

export default class GroceryList extends CacheableDataObject {
	beforeConstruct() {
		this.name = '';
		this.items = [];
		this.instanceNumber = --GroceryList.instanceCount;
		this.dates = new Map();
		this.is_active = false;
		this.purchased_on = null;
	}

	displayName() {
		if(this.name) {
			return this.name;
		}
		if(this.purchased_on) {
			return this.purchased_on.format('LL');
		}
		return `List ${this.created_at.format('LLL')}`;
	}

	isActive() {
		return this.is_active === true;
	}

	/** A list of what was bought on `purchased_on`, made by checking items off the active list. */
	isBought() {
		return !!this.purchased_on;
	}

	isArchived() {
		return this.isBought();
	}

	latestDate() {
		const map = this.dates;
		return Array.from(map)[map.size - 1][1];
	}

	/**
	 * For display purposes
	 * @returns {number}
	 */
	getItemCount() {
		return this.items.filter((item) => item.name !== '').length;
	}

	/**
	 * @param {boolean} allowNew  keep a blank row at the bottom to type a new item into. A bought
	 *        list is a record of a shop, so it has no such row.
	 */
	getListableItems(allowNew = true) {
		if(!allowNew) {
			return this.items.filter((item) => item.name !== '');
		}
		let items = [];
		const totalItems = _.size(this.items);
		let currentCount = 1;

		if(totalItems === 0) {
			this.addItem();
		}
		let streakWithBlank = 0;
		for(let item of this.items) {
			items.push(item);
			if(item.name === '') {
				streakWithBlank++;
			} else {
				streakWithBlank = 0;
			}
			if(currentCount === totalItems && item.name !== '') {
				this.addItem();
			}
			currentCount++;
		}
		if(streakWithBlank > 1) {
			items = items.slice(0, (items.length + 1) - streakWithBlank);
		}
		return items;
	}

	addItem() {
		this.items.push(GroceryListItem.create());
	}
}
GroceryList.prototype.dataLocationParsers = {
	id: (groceryList, data) => {
		groceryList.id = Number.parseInt(data.id) || null;
	},
	is_active: (groceryList, data) => {
		groceryList.is_active = data.is_active === true || data.is_active === 1 || data.is_active === '1';
	},
	purchased_on: (groceryList, data) => {
		groceryList.purchased_on = data.purchased_on ? moment(data.purchased_on, 'YYYY-MM-DD') : null;
	},
	created_at: (groceryList, data) => {
		groceryList.created_at = moment.unix(data.created_at);
	},
	dates: (groceryList, data) => {
		groceryList.dates.clear();
		for(let date of data.dates) {
			let dateData = {
				formatted: null,
				date: null,
			};
			if(date.year === null) {
				dateData.formatted = 'No Date';
				dateData.date = null;
			} else {
				dateData.date = moment().year(date.year).month(date.month - 1).date(date.day);
				dateData.formatted = dateData.date;
			}
			groceryList.dates.set(dateData.formatted, dateData.date);
		}
		groceryList.dates = new Map([...groceryList.dates.entries()].sort(([_, a], [__, b]) => {
			return a.isAfter(b) ? 1 : -1;
		}));
	},
	items: (groceryList, data) => {
		const tempListById = new Map();
		const tempListByReferenceId = {};
		const tempListByReference = new Set();
		for(let item of groceryList.items) { //Loop over my current list and set a bunch of references
			tempListById.set(item.id, item);
			if(tempListByReferenceId)
			{
				tempListByReferenceId[item.instanceNumber] = item;
			}
			tempListByReference.add(item);
		}
		const newItemList = [];
		for(let newItem of data.items) {
			if(newItem.id && (tempListById.has(newItem.id) || tempListByReferenceId[newItem.temporary_id])) {
				let itemToUpdate;
				if(tempListByReferenceId[newItem.temporary_id])
				{
					itemToUpdate = tempListByReferenceId[newItem.temporary_id];
				}
				else
				{
					itemToUpdate = tempListById.get(newItem.id);
				}

				itemToUpdate.update(newItem);
				newItemList.push(itemToUpdate);
				tempListByReference.delete(itemToUpdate);
			} else {
				newItemList.push(GroceryListItem.create(newItem));
			}
		}
		for(let item of tempListByReference)
		{
			if(item.id)
			{
				//Item with an id was not in the new list, so it was removed
				continue;
			}
			//These would correspond to items being edited by the user...
			newItemList.push(item);
		}


		groceryList.items = newItemList;
	}
};
GroceryList.instanceCount = 0;